import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom"; // Import useLocation
import "./styles/app.sass";
import Page from "./components/Page";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import SuperAdminRoute from "./components/SuperAdminRoute";
import StudentRoute from "./components/StudentRoute";
import CompanyRoute from "./components/CompanyRoute";
import CommercialRoute from "./components/CommercialRoute";
import PedagogicalRoute from "./components/PedagogicalRoute";
import TeacherRoute from "./components/TeacherRoute";
import StaffRoute from "./components/StaffRoute";
import RootRedirect from "./components/RootRedirect";
import NotFound from "./components/NotFound";

// Pages de connexion
import SignIn from "./screens/SignIn";
import RequestPasswordReset from "./screens/RequestPasswordReset";
import ConfirmPasswordReset from "./screens/ConfirmPasswordReset";
import AccountInactive from "./screens/AccountInactive";

// Settings
import Settings from "./screens/Settings";

//SuperAdmin
import Dashboard from "./screens/Dashboard";
import PartnerList from "./screens/PartnerList";
import PartnerDetail from "./screens/PartnerDetail";
import PartnerCreate from "./screens/PartnerCreate";
import ContactRequest from "./screens/ContactRequest";


// Admin
import Home from "./screens/Home";
import StudentDetail from "./screens/StudentDetail";
import StudentCreate from "./screens/StudentCreate";
import StudentList from "./screens/StudentList";
import CompanyList from "./screens/CompanyList";
import CompanyDetail from "./screens/CompanyDetail";
import CompanyCreate from "./screens/CompanyCreate";
import ProspectStudentList from "./screens/ProspectCandidatList";
import ProspectStudentDetail from "./screens/ProspectCandidatDetail";
import ProspectStudentCreate from "./screens/ProspectCandidatCreate";
import ProspectCompanyList from "./screens/ProspectCompanyList";
import ProspectCompanyDetail from "./screens/ProspectCompanyDetail";
import ProspectCompanyCreate from "./screens/ProspectCompanyCreate";
import CenterList from "./screens/CenterList";
import CenterCreate from "./screens/CenterCreate";
import CenterDetail from "./screens/CenterDetail";
import Notification from "./screens/Notification";
import Member from "./screens/Member";
import FormationList from "./screens/FormationList";
import FormationDetail from "./screens/FormationDetail";
import FormationCreate from "./screens/FormationCreate";
import FolderCreate from "./screens/FolderCreate";
import FolderCreateManual from "./screens/FolderCreateManual";
import FolderList from "./screens/FolderList";
import FolderDetail from "./screens/FolderDetail";
import InvoiceDetails from "./screens/InvoiceDetails";
import JobOfferList from "./screens/JobOfferList";
import JobOfferDetail from "./screens/JobOfferDetail";
import SessionsFormationList from "./screens/SessionsFormationList";
import SessionsFormationCreate from "./screens/SessionsFormationCreate";
import SessionsFormationDetail from "./screens/SessionsFormationDetail";
import ProspectDashboard from "./screens/ProspectDashboard";
import SessionsFormationDashboard from "./screens/SessionsFormationDashboard";
import SessionGroupsList from "./screens/SessionGroupsList";
import SessionGroupsDetail from "./screens/SessionGroupsDetail";
import SessionGroupsCreate from "./screens/SessionGroupsCreate";
import AbsencesList from "./screens/AbsencesList";

// Teacher
import SessionsFormationListTeacher from "./screens/SessionsFormationListTeacher";
import SessionsFormationDetailTeacher from "./screens/SessionsFormationDetailTeacher";

// Signature
import SignaturePage from "./screens/SignaturePage";

// Student
import AccountStudent from "./screens/AccountStudent";
import CompletAccountStudent from "./screens/CompletAccountStudent";
import StudentDashboard from "./screens/StudentDashboard";
import StudentCheckInformation from "./screens/StudentCheckInformation";

// Company
import AccountCompany from "./screens/AccountCompany";
import CompletAccountCompany from "./screens/CompletAccountCompany";
import AlternanceOfferCreate from "./screens/AlternanceOfferCreate";
import AlternanceOfferList from "./screens/AlternanceOfferList";
import AlternanceOfferDetail from "./screens/AlternanceOfferDetail";
import CompanyDashboard from "./screens/CompanyDashboard";
import CompanyContractsList from "./screens/CompanyContractsList";
import CompanyCheckInformation1 from "./screens/CompanyCheckInformation1";
import CompanyCheckInformation2 from "./screens/CompanyCheckInformation2";
import CompanyCheckInformation3 from "./screens/CompanyCheckInformation3";

// Partner Onboarding
import CompletAccountPartner from "./screens/CompletAccountPartner";
import CompletAccountAddFormation from "./screens/CompletAccountAddFormation";
import CompletAccountAddCenter from "./screens/CompletAccountAddCenter";
import AccountPartner from "./screens/AccountPartner";
import Faq from "./screens/Faq";

// Autres
import PageList from "./screens/PageList";

function App() {
    const location = useLocation(); // Use useLocation to get the current location

    return (
        <AuthProvider>
            <Routes>
                {/* Public Routes */}
                {/* Smart redirect from root based on authentication and role */}
                <Route path="/" element={<RootRedirect />} />
                <Route path="/sign-in" element={<SignIn />} />
                <Route path="/login" element={<Navigate to="/sign-in" replace />} />
                <Route path="/forgot-password" element={<RequestPasswordReset />} />
                <Route path="/reset-password" element={<ConfirmPasswordReset />} />
                <Route path="/account-inactive" element={<AccountInactive />} />




                {/* Special route to intercept and redirect PocketBase tokens */}
                <Route path="/auth/confirm-password-reset/:token" element={
                    <Navigate
                        to={`/reset-password?token=${location.pathname.split('/').pop()}`}
                        replace
                    />
                } />

                <Route element={<SuperAdminRoute />}>
                    <Route
                        path="/dashboard"
                        element={
                            <Page wide>
                                <Dashboard />
                            </Page>
                        }
                    />
                    <Route
                        path="/partners"
                        element={
                            <Page wide>
                                <PartnerList />
                            </Page>
                        }
                    />
                    <Route
                        path="/partners/:id"
                        element={
                            <Page wide>
                                <PartnerDetail />
                            </Page>
                        }
                    />
                    <Route
                        path="/partners/create"
                        element={
                            <Page wide>
                                <PartnerCreate />
                            </Page>
                        }
                    />
                    <Route
                        path="/contact-request"
                        element={
                            <Page wide>
                                <ContactRequest />
                            </Page>
                        }
                    />
                </Route>

                {/* Staff Routes (Admin, Pedagogical, Commercial) */}
                <Route element={<StaffRoute />}>

                    {/* Partner Onboarding Routes */}
                    <Route
                        path="/partner/complete-account"
                        element={<CompletAccountPartner />}
                    />
                    <Route
                        path="/partner/add-formation"
                        element={<CompletAccountAddFormation />}
                    />
                    <Route
                        path="/partner/add-center"
                        element={<CompletAccountAddCenter />}
                    />
                    <Route
                        path="/partner/settings"
                        element={
                            <Page wide>
                                <AccountPartner />
                            </Page>
                        }
                    />
                    <Route
                        path="/partner/faq"
                        element={
                            <Page wide>
                                <Faq />
                            </Page>
                        }
                    />

                    <Route
                        path="/admin/dashboard"
                        element={
                            <Page wide>
                                <Home />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/students"
                        element={
                            <Page wide>
                                <StudentList />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/students/create"
                        element={
                            <Page wide>
                                <StudentCreate />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/students/:id"
                        element={
                            <Page wide>
                                <StudentDetail />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/companies"
                        element={
                            <Page wide>
                                <CompanyList />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/companies/:id"
                        element={
                            <Page wide>
                                <CompanyDetail />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/companies/create"
                        element={
                            <Page wide>
                                <CompanyCreate />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/prospects_student"
                        element={
                            <Page wide>
                                <ProspectStudentList />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/prospects_student/:id"
                        element={
                            <Page wide>
                                <ProspectStudentDetail />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/prospects_student/create"
                        element={
                            <Page wide>
                                <ProspectStudentCreate />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/prospects_company"
                        element={
                            <Page wide>
                                <ProspectCompanyList />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/prospects_company/:id"
                        element={
                            <Page wide>
                                <ProspectCompanyDetail />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/prospects_company/create"
                        element={
                            <Page wide>
                                <ProspectCompanyCreate />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/centers"
                        element={
                            <Page wide>
                                <CenterList />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/centers/:id"
                        element={
                            <Page wide>
                                <CenterDetail />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/centers/create"
                        element={
                            <Page wide>
                                <CenterCreate />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/settings"
                        element={
                            <Page title="Paramètres">
                                <Settings />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/notifications"
                        element={
                            <Page wide>
                                <Notification />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/members"
                        element={
                            <Page wide>
                                <Member />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/formations"
                        element={
                            <Page wide>
                                <FormationList />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/formations/:id"
                        element={
                            <Page wide>
                                <FormationDetail />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/formations/create"
                        element={
                            <Page wide>
                                <FormationCreate />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/alternance-offers"
                        element={
                            <Page wide>
                                <JobOfferList />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/alternance-offers/:id"
                        element={
                            <Page wide>
                                <JobOfferDetail />
                            </Page>
                        }
                    />
                    
                    <Route
                        path="/admin/folders/"
                        element={
                            <Page wide>
                                <FolderList />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/folders/create"
                        element={
                            <Page title="Nouveau dossier">
                                <FolderCreate />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/folders/create/auto"
                        element={
                            <Page title="Nouveau dossier">
                                <FolderCreate />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/folders/create/manual"
                        element={
                            <Page title="Nouveau dossier">
                                <FolderCreateManual />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/folders/:id"
                        element={
                            <Page wide>
                                <FolderDetail />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/invoice-details"
                        element={
                            <Page wide>
                                <InvoiceDetails />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/sessions-formation"
                        element={
                            <Page wide>
                                <SessionsFormationList />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/sessions-formation/create"
                        element={
                            <Page wide>
                                <SessionsFormationCreate />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/sessions-formation/:id"
                        element={
                            <Page wide>
                                <SessionsFormationDetail />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/prospect-dashboard"
                        element={
                            <Page wide>
                                <ProspectDashboard />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/sessions-formation-dashboard"
                        element={
                            <Page wide>
                                <SessionsFormationDashboard />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/session-groups"
                        element={
                            <Page wide>
                                <SessionGroupsList />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/session-groups/:id"
                        element={
                            <Page wide>
                                <SessionGroupsDetail />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/session-groups/create"
                        element={
                            <Page wide>
                                <SessionGroupsCreate />
                            </Page>
                        }
                    />
                    <Route
                        path="/admin/absences"
                        element={
                            <Page wide>
                                <AbsencesList />
                            </Page>
                        }
                    />

                </Route>

                {/* Student Routes */}
                <Route element={<StudentRoute />}>
                    <Route
                        path="/student/account"
                        element={
                            <Page wide>
                                <AccountStudent />
                            </Page>
                        }
                    />
                    <Route
                        path="/complete-account"
                        element={<CompletAccountStudent />}
                    />
                    <Route
                        path="/student/settings"
                        element={
                            <Page title="Paramètres">
                                <Settings />
                            </Page>
                        }
                    />
                    <Route
                        path="/student/notifications"
                        element={
                            <Page wide>
                                <Notification />
                            </Page>
                        }
                    />
                    <Route
                        path="/student/dashboard"
                        element={
                            <Page wide>
                                <StudentDashboard />
                            </Page>
                        }
                    />
                    <Route
                        path="/student/folder/:id/edit"
                        element={
                            <Page title="Dossier">
                                <StudentCheckInformation />
                            </Page>
                        }
                    />
                    <Route
                        path="/signature/:attendanceId"
                        element={
                            <Page wide>
                                <SignaturePage />
                            </Page>
                        }
                    />
                    <Route
                        path="/student/planning"
                        element={
                            <Page wide>
                                
                            </Page>
                        }
                    />
                </Route>

                {/* Company Routes */}
                <Route element={<CompanyRoute />}>
                    <Route
                        path="/company/account"
                        element={
                            <Page wide>
                                <AccountCompany />
                            </Page>
                        }
                    />
                    <Route
                        path="/account/complete-account"
                        element={<CompletAccountCompany />}
                    />
                    <Route
                        path="/company/settings"
                        element={
                            <Page title="Paramètres">
                                <Settings />
                            </Page>
                        }
                    />
                    <Route
                        path="/company/notifications"
                        element={
                            <Page wide>
                                <Notification />
                            </Page>
                        }
                    />
                    <Route
                        path="/company/alternance-offers/add"
                        element={
                            <Page wide>
                                <AlternanceOfferCreate />
                            </Page>
                        }
                    />
                    <Route
                        path="/company/alternance-offers"
                        element={
                            <Page wide>
                                <AlternanceOfferList />
                            </Page>
                        }
                    />
                    <Route
                        path="/company/alternance-offers/:id"
                        element={
                            <Page wide>
                                <AlternanceOfferDetail />
                            </Page>
                        }
                    />
                    <Route
                        path="/company/dashboard"
                        element={
                            <Page wide>
                                <CompanyDashboard />
                            </Page>
                        }
                    />
                    <Route
                        path="/company/contracts"
                        element={
                            <Page wide>
                                <CompanyContractsList />
                            </Page>
                        }
                    />
                    <Route
                        path="/company/folder/:id/1"
                        element={
                            <Page title="L'entreprise">
                                <CompanyCheckInformation1 />
                            </Page>
                        }
                    />
                    <Route
                        path="/company/folder/:id/2"
                        element={
                            <Page title="Contrat">
                                <CompanyCheckInformation2 />
                            </Page>
                        }
                    />
                    <Route
                        path="/company/folder/:id/3"
                        element={
                            <Page title="Maître(s) d'apprentissage">
                                <CompanyCheckInformation3 />
                            </Page>
                        }
                    />
                </Route>

                {/* Pedagogical Routes */}
                <Route element={<PedagogicalRoute />}>
                    <Route
                        path="/pedagogical/dashboard"
                        element={
                            <Page wide>
                                <Home />
                            </Page>
                        }
                    />
                    <Route
                        path="/pedagogical/folders"
                        element={
                            <Page wide>
                                <FolderList />
                            </Page>
                        }
                    />
                    <Route
                        path="/pedagogical/folders/create"
                        element={
                            <Page title="Nouveau dossier">
                                <FolderCreate />
                            </Page>
                        }
                    />
                    <Route
                        path="/pedagogical/folders/create/auto"
                        element={
                            <Page title="Nouveau dossier automatique">
                                <FolderCreate />
                            </Page>
                        }
                    />
                    <Route
                        path="/pedagogical/folders/create/manual"
                        element={
                            <Page title="Nouveau dossier manuel">
                                <FolderCreateManual />
                            </Page>
                        }
                    />
                    <Route
                        path="/pedagogical/folders/:id"
                        element={
                            <Page wide>
                                <FolderDetail />
                            </Page>
                        }
                    />
                    <Route
                        path="/pedagogical/invoice-details"
                        element={
                            <Page wide>
                                <InvoiceDetails />
                            </Page>
                        }
                    />
                    <Route
                        path="/pedagogical/settings"
                        element={
                            <Page title="Paramètres">
                                <Settings />
                            </Page>
                        }
                    />
                    <Route
                        path="/pedagogical/notifications"
                        element={
                            <Page wide>
                                <Notification />
                            </Page>
                        }
                    />
                    <Route
                        path="/pedagogical/students"
                        element={
                            <Page wide>
                                <StudentList />
                            </Page>
                        }
                    />
                    <Route
                        path="/pedagogical/students/create"
                        element={
                            <Page wide>
                                <StudentCreate />
                            </Page>
                        }
                    />
                    <Route
                        path="/pedagogical/students/:id"
                        element={
                            <Page wide>
                                <StudentDetail />
                            </Page>
                        }
                    />
                    <Route
                        path="/pedagogical/companies"
                        element={
                            <Page wide>
                                <CompanyList />
                            </Page>
                        }
                    />
                    <Route
                        path="/pedagogical/companies/:id"
                        element={
                            <Page wide>
                                <CompanyDetail />
                            </Page>
                        }
                    />
                    <Route
                        path="/pedagogical/companies/create"
                        element={
                            <Page wide>
                                <CompanyCreate />
                            </Page>
                        }
                    />
                    <Route
                        path="/pedagogical/centers"
                        element={
                            <Page wide>
                                <CenterList />
                            </Page>
                        }
                    />
                    <Route
                        path="/pedagogical/centers/:id"
                        element={
                            <Page wide>
                                <CenterDetail />
                            </Page>
                        }
                    />
                    <Route
                        path="/pedagogical/centers/create"
                        element={
                            <Page wide>
                                <CenterCreate />
                            </Page>
                        }
                    />
                    <Route
                        path="/pedagogical/formations"
                        element={
                            <Page wide>
                                <FormationList />
                            </Page>
                        }
                    />
                    <Route
                        path="/pedagogical/formations/:id"
                        element={
                            <Page wide>
                                <FormationDetail />
                            </Page>
                        }
                    />
                    <Route
                        path="/pedagogical/formations/create"
                        element={
                            <Page wide>
                                <FormationCreate />
                            </Page>
                        }
                    />
                </Route>

                {/* Commercial Routes */}
                <Route element={<CommercialRoute />}>
                    <Route
                        path="/commercial/settings"
                        element={
                            <Page title="Paramètres">
                                <Settings />
                            </Page>
                        }
                    />
                    <Route
                        path="/commercial/notifications"
                        element={
                            <Page wide>
                                <Notification />
                            </Page>
                        }
                    />
                    <Route
                        path="/commercial/prospects_student"
                        element={
                            <Page wide>
                                <ProspectStudentList />
                            </Page>
                        }
                    />
                    <Route
                        path="/commercial/prospects_student/:id"
                        element={
                            <Page wide>
                                <ProspectStudentDetail />
                            </Page>
                        }
                    />
                    <Route
                        path="/commercial/prospects_student/create"
                        element={
                            <Page wide>
                                <ProspectStudentCreate />
                            </Page>
                        }
                    />
                    <Route
                        path="/commercial/prospects_company"
                        element={
                            <Page wide>
                                <ProspectCompanyList />
                            </Page>
                        }
                    />
                    <Route
                        path="/commercial/prospects_company/:id"
                        element={
                            <Page wide>
                                <ProspectCompanyDetail />
                            </Page>
                        }
                    />
                    <Route
                        path="/commercial/prospects_company/create"
                        element={
                            <Page wide>
                                <ProspectCompanyCreate />
                            </Page>
                        }
                    />
                    <Route
                        path="/commercial/alternance-offers"
                        element={
                            <Page wide>
                                <JobOfferList />
                            </Page>
                        }
                    />
                    <Route
                        path="/commercial/alternance-offers/:id"
                        element={
                            <Page wide>
                                <JobOfferDetail />
                            </Page>
                        }
                    />
                    <Route
                        path="/commercial/prospect-dashboard"
                        element={
                            <Page wide>
                                <ProspectDashboard />
                            </Page>
                        }
                    />
                </Route>

                {/* Teacher Routes */}
                <Route element={<TeacherRoute />}>
                    <Route
                        path="/teacher/sessions-formation"
                        element={
                            <Page wide>
                                <SessionsFormationListTeacher />
                            </Page>
                        }
                    />
                    <Route
                        path="/teacher/sessions-formation/:id"
                        element={
                            <Page wide>
                                <SessionsFormationDetailTeacher />
                            </Page>
                        }
                    />
                </Route>

                {/* General Protected Routes (accessible to all authenticated users) */}
                <Route element={<ProtectedRoute />}>
                    <Route path="/pages" element={<PageList />} />
                </Route>

                {/* Catch-all route for unmatched URLs */}
                <Route path="*" element={<NotFound />} />
            </Routes>
        </AuthProvider>
    );
}

export default App;